import * as C from './constants.js'
import { POWERUP_SLOTS } from './constants.js'
import { forwardAim, fullBounds, hubSpawn, isOnPad, loadArena, matchSpawn } from './arena.js'
import { seedRng } from './rng.js'
import { BASE_TUNING, discTuningFrom, resolveTuning, type TuningOverlay } from './tuning.js'
import { overlaysFor } from './powerups.js'
import type { DiscState, MatchKind, MatchState, PlayerState, PlayerStats, SimMode, SimOptions, SimState, Team, Vec2 } from './types.js'

export const emptyStats = (): PlayerStats => ({
  eliminations: 0, bankEliminations: 0, perfectDeflects: 0, lateBlocks: 0, throws: 0,
  catches: 0, steals: 0, dashes: 0, jumps: 0, shieldTicks: 0, survived: 0, deaths: 0,
})

export interface CreateOptions {
  mode?: SimMode
  kind?: MatchKind
  seed?: number
  matchId?: string
  arenaId?: string
  options?: Partial<SimOptions>
  /** 13 §4 playtest knobs (see SimState.knobs). */
  knobs?: TuningOverlay | null
}

export function createState(o: CreateOptions = {}): SimState {
  const mode = o.mode ?? 'hub'
  const arena = loadArena(o.arenaId)
  const match: MatchState = {
    phase: mode === 'hub' ? 'LIVE' : 'RESET',
    phaseTicks: 0,
    lobby: { decks: {}, locks: {} },
    roundNumber: 0,
    roundIndex: 0,
    score: [0, 0],
    voidRounds: 0,
    timerTicks: 0,
    overtimeTicks: 0,
    bounds: { 0: fullBounds(arena, 0), 1: fullBounds(arena, 1) },
    roundResults: [],
    winner: null,
    coinUsed: false,
    paused: false,
    roundTicks: 0,
  }
  return {
    tick: 0,
    mode,
    kind: o.kind ?? (mode === 'hub' ? 'practice' : 'pvp'),
    matchId: o.matchId ?? 'local',
    rng: seedRng(o.seed ?? 1),
    arena,
    players: [],
    discs: [],
    nextDiscId: 1,
    match,
    options: { powerups: false, ...o.options },
    ...(o.knobs && Object.keys(o.knobs).length ? { knobs: { ...o.knobs } } : {}),
    events: [],
  }
}

export const cloneState = (s: SimState): SimState => structuredClone(s)

export function findPlayer(s: SimState, id: string): PlayerState | undefined {
  for (const p of s.players) if (p.id === id) return p
  return undefined
}
export function findDisc(s: SimState, id: number): DiscState | undefined {
  for (const d of s.discs) if (d.id === id) return d
  return undefined
}
export const playerIndex = (s: SimState, id: string): number => s.players.findIndex((p) => p.id === id)

const byId = <T extends { id: string | number }>(a: T, b: T): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/** Adds a player (idempotent on id). Keeps `players` sorted. In hub mode the
 *  player spawns immediately with discs + invulnerability; in match mode
 *  placement happens at RESET. */
export function addPlayer(s: SimState, id: string, team: Team, name = id, powerUps: string[] = [], overlays: TuningOverlay[] = [], isBot = false, at?: Vec2, imprints: string[] = [], slots: number = POWERUP_SLOTS): PlayerState {
  const existing = findPlayer(s, id)
  if (existing) return existing
  const slot = s.players.filter((p) => p.team === team).length
  const { tuning } = resolveTuning(overlays, s.knobs)
  const p: PlayerState = {
    id, name, team, slot,
    pos: { x: 0, y: s.arena.floorY, z: 0 },
    vel: { x: 0, y: 0, z: 0 },
    aim: forwardAim(team),
    state: 'IDLE', stateTicks: 0,
    stamina: tuning.STAMINA_MAX,
    regenDelayTicks: 0, dashCooldownTicks: 0, shieldLockoutTicks: 0, shieldAgeTicks: 0,
    throwAim: forwardAim(team), dashDir: forwardAim(team),
    discsHeld: [], maxDiscs: tuning.MAX_DISCS_HELD_BASE,
    alive: true, grounded: true, landTicks: 0,
    invulnUntilTick: 0, respawnTick: 0,
    powerUps: [...powerUps, ...imprints], imprints: [...imprints], slots, tuning, isBot, absent: false,
    hurtboxes: [{ dx: 0, dz: 0, r: tuning.PLAYER_RADIUS }],
    prevButtons: { throw: false, dash: false, jump: false },
    lastInputSeq: -1,
    roundStats: emptyStats(), matchStats: emptyStats(),
  }
  s.players.push(p)
  s.players.sort(byId)
  if (s.mode === 'hub') spawnInHub(s, p, at)
  return p
}

export function removePlayer(s: SimState, id: string): boolean {
  const i = s.players.findIndex((p) => p.id === id)
  if (i < 0) return false
  const p = s.players[i]
  // Held discs drop; flying/returning discs keep their thread until timeout.
  for (const d of s.discs) {
    if (d.holder === id) { d.holder = null; if (d.state === 'HELD') { d.state = 'GROUND'; d.stateTicks = 0; d.vel = { x: 0, y: 0, z: 0 }; d.pos.y = 0 } }
    if (d.homeOwner === id) d.despawnOnTimeout = true
  }
  s.players.splice(i, 1)
  // Re-slot the departed player's teammates so slots stay contiguous (hub only;
  // match slots are stable per 02 §6 and a match with an empty seat keeps it).
  if (s.mode === 'hub') {
    let k = 0
    for (const q of s.players) if (q.team === p.team) q.slot = k++
  }
  return true
}

/** Issue a fresh disc held by `p`. */
export function issueDisc(s: SimState, p: PlayerState): DiscState {
  const d: DiscState = {
    id: s.nextDiscId++,
    homeOwner: p.id, holder: p.id, thrower: null, team: p.team,
    state: 'HELD', stateTicks: 0,
    pos: { x: p.pos.x, y: C.DISC_FLIGHT_Y, z: p.pos.z },
    vel: { x: 0, y: 0, z: 0 },
    rangeLeft: 0, bounceCount: 0, homing: false,
    tuning: discTuningFrom(p.tuning),
    despawnOnTimeout: false, lastZoneTeam: p.team,
  }
  s.discs.push(d)
  s.discs.sort(byId)
  p.discsHeld.push(d.id)
  return d
}

/** Reset a player's per-round combat fields (RESET / hub respawn). */
export function resetPlayerCombat(p: PlayerState, team: Team): void {
  p.vel = { x: 0, y: 0, z: 0 }
  p.aim = forwardAim(team)
  p.throwAim = forwardAim(team)
  p.state = 'IDLE'
  p.stateTicks = 0
  p.stamina = p.tuning.STAMINA_MAX
  p.regenDelayTicks = 0
  p.dashCooldownTicks = 0
  p.shieldLockoutTicks = 0
  p.shieldAgeTicks = 0
  p.discsHeld = []
  p.maxDiscs = p.tuning.MAX_DISCS_HELD_BASE
  p.alive = true
  p.grounded = true
  p.landTicks = 0
  p.respawnTick = 0
  p.prevButtons = { throw: false, dash: false, jump: false }
}

/** Hub spawn/respawn (10 §1, 02 §6): preferred position if it is on a pad
 *  (post-match return, spec §1), else the slot with fewest neighbours; invuln, fresh discs. */
export function spawnInHub(s: SimState, p: PlayerState, at?: Vec2): void {
  const sp = at && isOnPad(s.arena, at.x, at.z) ? at : hubSpawn(s.arena, p.team, s.players.filter((q) => q.id !== p.id))
  p.pos = { x: sp.x, y: s.arena.floorY, z: sp.z }
  applyTuning(p, overlaysFor(p.powerUps.slice(0, p.slots)), s.knobs) // hub loadout applies on (re)spawn only (06 §6); slots per 12 §8.2
  scaleMove(p, s.arena.hub.moveMul * (s.arena.moveMul ?? 1))
  scaleDisc(p, s.arena.discMul ?? 1)
  resetPlayerCombat(p, p.team)
  p.invulnUntilTick = s.tick + Math.round(C.HUB_SPAWN_INVULN_S * C.SIM_HZ)
  // Old discs that belonged to this player become litter (03 §8).
  for (const d of s.discs) if (d.homeOwner === p.id && d.state !== 'DESPAWN') d.despawnOnTimeout = true
  for (let i = 0; i < p.maxDiscs; i++) issueDisc(s, p)
}

/** Match RESET placement (02 §6): slot spawn, facing the gap, discs issued. */
export function placeForRound(s: SimState): void {
  for (const p of s.players) {
    const teamSize = s.players.filter((q) => q.team === p.team).length
    const sp = matchSpawn(s.arena, p.team, p.slot, teamSize)
    p.pos = { x: sp.x, y: s.arena.floorY, z: sp.z }
    applyTuning(p, overlaysFor(p.powerUps.slice(0, p.slots)), s.knobs) // recomputed at every RESET, never cached (06 §8); slots per 12 §8.2
    scaleMove(p, s.arena.moveMul ?? 1)
    scaleDisc(p, s.arena.discMul ?? 1)
    resetPlayerCombat(p, p.team)
    p.invulnUntilTick = 0
    p.roundStats = emptyStats()
    // 10 §6: an ABSENT seat is placed but stays derezzed with no discs until
    // the player is back (re-seated at the next RESET).
    if (p.absent) { p.alive = false; p.state = 'DEREZ'; p.discsHeld = [] }
  }
  s.discs = []
  for (const p of s.players) if (!p.absent) for (let i = 0; i < p.maxDiscs; i++) issueDisc(s, p)
}

/** Re-resolve tuning from the player's power-up list (called at RESET — 12 §8.1/8.2). */
export function applyTuning(p: PlayerState, overlays: TuningOverlay[], knobs?: TuningOverlay | null): void {
  const { tuning } = resolveTuning(overlays, knobs)
  p.tuning = tuning
  p.hurtboxes = [{ dx: 0, dz: 0, r: tuning.PLAYER_RADIUS }]
  p.maxDiscs = tuning.MAX_DISCS_HELD_BASE
}

/** Hub traversal multiplier (spec §1). Applied AFTER the overlay resolve so it
 *  composes with Sprinter/Long Arm instead of colliding with their MOVE_SPEED
 *  under the disjoint-key rule (06 §1). No-op at 1. */
export function scaleMove(p: PlayerState, mul: number): void {
  if (mul === 1) return
  p.tuning = { ...p.tuning, MOVE_SPEED: p.tuning.MOVE_SPEED * mul, DASH_SPEED: p.tuning.DASH_SPEED * mul }
}
/** Per-arena disc speed (Arena.discMul); the disc inherits the thrower's tuning at release, so scaling it here covers every throw. */
export function scaleDisc(p: PlayerState, mul: number): void {
  if (mul === 1) return
  p.tuning = { ...p.tuning, DISC_THROW_SPEED: p.tuning.DISC_THROW_SPEED * mul }
}

/** Hub loadout (06 §6): stored now, applied at the next respawn. */
export function applyHubLoadout(s: SimState, playerId: string, ids: readonly string[]): boolean {
  const p = findPlayer(s, playerId)
  if (!p) return false
  p.powerUps = [...ids.filter((id) => overlaysFor([id]).length > 0).slice(0, 1), ...p.imprints]
  return true
}

export { BASE_TUNING }

/** Stable fingerprint of a state (rounded floats, FNV-1a). */
export function hashState(s: SimState): string {
  const str = JSON.stringify(s, (k, v) => (k === 'events' ? undefined : typeof v === 'number' ? Math.round(v * 1e6) / 1e6 : v))
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}
